import { setNewRelicUserId } from '@/external_scripts/newrelicUser.ts'
import CookieHelper from '@/services/helpers/CookieHelper'
import { defineStore } from 'pinia'
import { nextTick } from 'vue'

const ACCESS_TOKEN_EXPIRATION = 1000 * 60 * 59
const SOFT_LOGIN_COOKIE = 'soft_login'
const NR_SOURCE_AUTH_LOGIN_UNKNOWN = 'auth_login_unknown'
const NR_SOURCE_AUTH_2FA_UNKNOWN = 'auth_2fa_unknown'
const NR_SOURCE_AUTH_CONFIRM_2FA_UNKNOWN = 'auth_confirm_2fa_unknown'
const LOGIN_RATE_LIMITED_MESSAGE = 'login_error_rate_limited'
const LOGIN_RATE_LIMITED_EXPIRATION = 10000
const NETWORK_ERROR_REGEX = /(Failed to fetch|Load failed|NetworkError)/
const LAST_REFRESH = 'last_refresh'
const API_KEY_AGE = 'apiKey_age'
const API_KEY = 'apiKey'
// in-flight braintree client token request, kept outside the store state (non-reactive)
let _paymentTokenRequest = null

// Too many logins from one IP make Cloudflare answer with a challenge page (429) for
// 10 minutes. It carries no CORS headers, so the browser withholds the 429 and fetch
// fails like a dropped connection. Both are expected and not reported (VIP-12639).
const _isLoginRateLimited = ({ status, message } = {}) =>
  status === 429 ||
  (status === undefined && NETWORK_ERROR_REGEX.test(message ?? ''))

const _showLoginRateLimited = (MessageHelper) =>
  MessageHelper.addErrorMessage(
    LOGIN_RATE_LIMITED_MESSAGE,
    null,
    LOGIN_RATE_LIMITED_EXPIRATION
  )

// The v2 profile answered anonymously: the access token did not identify a customer.
class AnonymousProfileError extends Error {}

// The customer profile from GET /v2/customer/profile — the single profile source since
// VIP-12253. This empty default gives the store state its field shape (for typed consumers)
// before login and on logout; the real values arrive from the v2 response.
const _emptyProfile = () => ({
  id: null,
  customer_id: null,
  email: null,
  first_name: null,
  last_name: null,
  salutation: null,
  is_admin: null,
  admin_data: null,
  meta_navigation: null,
  credits_formatted: null,
  first_expiring_credit_days_left: null,
  completed_orders_count: null,
  days_since_last_order: null,
  is_newsletter_subscribed: null,
  doi_show_checkbox: null,
  dob: null,
  ip_address: null,
  phone: null,
  miles_more_card_number: null
})
export const useCustomerStore = defineStore('customer', {
  state() {
    return {
      pending: false,
      profile: _emptyProfile(),
      profileV2: null,
      addresses: null,
      paymentMethods: null,
      payment: {
        token: null
      },
      active: false,
      softLoggedIn: false,
      isLoginShowing: false,
      lastSoftLoginCookie: null,
      adminPermissionError: false,
      presets: {
        email: null,
        password: null,
        totpSetupRequired: false
      },
      hooks: {
        afterLogin: null,
        afterLogout: null,
        afterRegister: null
      }
    }
  },
  getters: {
    customerProfile: ({ profile, profileV2 }) => {
      const originalCustomerId = CookieHelper.getCookie('original-customer-id')
      return originalCustomerId ? profileV2 : profile
    },
    customerSoftLoggedIn: ({ active, softLoggedIn }) => active || softLoggedIn,
    customerId() {
      return (
        this.customerProfile?.customer_id ?? this.customerProfile?.id ?? null
      )
    },
    customerActive: ({ active }) => active,
    customerIsAdmin: ({ profile }) => profile.is_admin || !!profile.admin_data,
    customerIsPending: ({ pending }) => pending,
    customerPaymentToken: ({ payment: { token } }) => token,
    adminLoggedInAs: ({ profile }) => {
      // First check profile data (for backward compatibility)
      if (profile?.admin_data?.admin_customer_id) {
        return profile.admin_data.admin_customer_id
      }

      // Then check cookie (for new admin impersonation feature)
      if (typeof document !== 'undefined') {
        try {
          const cookieValue = CookieHelper.getCookie('original-customer-id')
          return cookieValue || null
        } catch (error) {
          console.error(error, 'Original customer id cookie not found')
          return null
        }
      }
      return null
    },
    isNewsletterSubscribed: ({
      profile: { is_newsletter_subscribed: isNewsletterSubscribed }
    }) => isNewsletterSubscribed,
    customerPresets: ({ presets }) => presets,
    hasDob: ({ profile: { dob } }) => !!dob,
    showAdminPermissionError: ({ adminPermissionError }) =>
      adminPermissionError,
    salutation: ({ profileV2 }) => profileV2?.salutation,
    // Refer-a-friend gate for the invitations nav item (VIP-12084), mirroring the legacy
    // two conditions: a completed order AND the site's refer-a-friend flag. Both come from
    // the v2 profile (per-site config, not a gatekeeper feature).
    completedOrdersCount: ({ profile }) =>
      Number(profile?.completed_orders_count) || 0,
    referralEnabled: ({ profileV2 }) => !!profileV2?.referral_enabled
  },
  actions: {
    async establishSession() {
      // already in progress, bail out
      if (this.pending) return

      this.pending = true
      // check if there has been an active session previously
      await this.checkPreviousSession()
      // check for soft login
      await this.checkSoftLogin()

      // no soft login nor active session, bail out
      if (!this.active && !this.softLoggedIn) {
        this.pending = false
        return
      }

      // Fetch silently: an expired v2 access token returns an anonymous profile, which we
      // recover below before treating it as a failure (VIP-12431). Capture the error so a
      // non-recovery bail still reports the concrete cause (e.g. a 502), not a generic message.
      let fetchError = null
      try {
        await this.fetchProfile(false, { silent: true, rethrow: true })
      } catch (e) {
        fetchError = e
      }

      // VIP-12431: the v2 access token expires after ~1h and is only refreshed in this flow,
      // so once it lapses the profile comes back anonymous. Recover it, then return: recovery
      // already minted a fresh token, so the proactive refresh below is not needed. (!softLoggedIn
      // guards intent — a soft-login session never does access-token refresh.)
      if (!this.profile.customer_id && this.active && !this.softLoggedIn) {
        const { error, sessionDead } = await this._recoverExpiredSession()
        if (sessionDead) return this._logOutDeadSession(error)
        if (!this.profile.customer_id) return this._abandonSession(error)
        this.pending = false
        return
      }

      if (!this.profile.customer_id) return this._abandonSession(fetchError)

      try {
        // get a fresh access token, if soft login: nothing to do here
        await _handleRefreshToken({
          profile: this.profile,
          softLoggedIn: this.softLoggedIn
        })
      } catch (e) {
        await _handleError(e)
      }

      this.pending = false
    },

    // Mint a fresh v2 access token and re-fetch the profile (VIP-12431). The session is dead
    // only when api-v2 refuses the refresh_token or the profile stays anonymous; a 5xx or a
    // network error is not, so the next establishSession can still recover.
    async _recoverExpiredSession() {
      const { ApiController } = await _getModules()
      try {
        await ApiController.refreshCustomerToken()
      } catch (e) {
        return {
          error: e,
          sessionDead: DEAD_REFRESH_TOKEN_CODES.has(e?.cause)
        }
      }
      try {
        await this.fetchProfile(true, { silent: true, rethrow: true })
        return { error: null, sessionDead: false }
      } catch (e) {
        return { error: e, sessionDead: e instanceof AnonymousProfileError }
      }
    },

    // checkPreviousSession re-derives `active` from apiKey_age, so a dead session left in
    // place would retry the refresh on every navigation; only the platform logout clears
    // it, httpOnly cookies included. Stays pending so the boot's other establishSession
    // calls do not retry before the redirect lands.
    async _logOutDeadSession(error) {
      console.error(error)
      this.active = false
      this.softLoggedIn = false
      await _logoutViaPlatform()
    },

    // Give up and reset the session. Logged once (preferring the concrete error) so a session
    // that recovered stays quiet. Also covers the app being recreated while a httpOnly cookie
    // still briefly reads as set (~1s).
    _abandonSession(error = null) {
      console.error(
        error ??
          new Error(
            'Session could not be established: v2 profile has no customer_id'
          )
      )
      this.pending = false
      this.active = false
      this.softLoggedIn = false
    },
    // Returns whether the profile is in a known-good state afterwards: true when the fetch
    // ran and succeeded (or was skipped because the cache is already current), false when it
    // was attempted and failed. Callers that forced a refetch to reflect a just-saved change
    // rely on this - a swallowed failure otherwise looks like success over stale data
    // (VIP-12085 review).
    async fetchProfile(
      force = false,
      { silent = false, rethrow = false } = {}
    ) {
      if (!(await this._shouldRefetchProfile(force))) return true

      try {
        await this._fetchProfileData()
        await this._updateSoftLoginReference()
        await this._initializeProfile()
        return true
      } catch (e) {
        this.softLoggedIn = false
        // establishSession's VIP-12431 recovery fetches silently and logs a genuine
        // failure once itself; every other caller keeps logging here as before.
        if (!silent) console.error(e)
        // ...and rethrows only when the caller wants to capture the concrete error
        // (the recovery path) rather than treat a false return as success.
        if (rethrow) throw e
        return false
      }
    },

    async _shouldRefetchProfile(force) {
      if (force) return true
      // If no customer_id, always refetch
      if (!this.profile.customer_id) return true
      // If not soft logged in and not active, don't refetch
      if (!this.softLoggedIn && !this.active) return false
      // If active (fully logged in), don't refetch unless forced
      if (this.active && !force) return false

      // For soft login users, check if cookie changed
      const { CookieHelper } = await _getModules()
      const currentSoftLoginCookie = CookieHelper.getCookie(SOFT_LOGIN_COOKIE)

      if (
        currentSoftLoginCookie &&
        currentSoftLoginCookie !== this.lastSoftLoginCookie
      ) {
        this.lastSoftLoginCookie = currentSoftLoginCookie
        return true
      }

      return false
    },

    async _fetchProfileData() {
      const { ApiController } = await _getModules()

      // v2 is now the only profile source (VIP-12253). It returns every field the app
      // still reads, so `profile` and `profileV2` both point at the same response.
      const profile = await ApiController.fetchCustomerProfileV2()
      if (!profile?.customer_id)
        throw new AnonymousProfileError('Invalid api key')

      this.profile = profile
      this.profileV2 = profile
      setNewRelicUserId(this.customerId)
    },

    async _updateSoftLoginReference() {
      if (this.softLoggedIn) {
        const { CookieHelper } = await _getModules()
        this.lastSoftLoginCookie = CookieHelper.getCookie(SOFT_LOGIN_COOKIE)
      }
    },

    async _initializeProfile() {
      await nextTick()
      await _initTagManager(this.profile)
    },
    async fetchPaymentToken(force = false) {
      // force is used when a cached token turned out to be expired
      if (force) this.payment.token = null
      // token is already setup, bail out
      if (this.payment.token) return this.payment.token
      // deduplicate concurrent callers (e.g. multiple express buttons mounting at once)
      if (!_paymentTokenRequest) {
        _paymentTokenRequest = (async () => {
          const { ApiController } = await _getModules()
          const { braintree_client_token: bct } =
            await ApiController.fetchCustomerBraintreeToken()
          if (!bct) throw new Error('Braintree client token missing')
          return bct
        })().finally(() => {
          _paymentTokenRequest = null
        })
      }
      // callers must use the returned token; re-reading the store after the
      // await could race with a concurrent force call nulling it
      const token = await _paymentTokenRequest
      this.payment.token = token
      return token
    },
    async waitUntilPending() {
      while (this.customerIsPending) await _wait()
    },
    async checkPreviousSession() {
      const { CookieHelper } = await _getModules()
      const active = !!CookieHelper.getCookie(API_KEY_AGE)
      if (active) {
        CookieHelper.invalidate(SOFT_LOGIN_COOKIE)
      }
      this.active = active
    },
    async checkSoftLogin() {
      if (this.active) return
      this.softLoggedIn = await _handleSoftLogin()
    },
    async logout() {
      const { ApiController, CookieHelper } = await _getModules()
      await ApiController.logoutCustomer()
      CookieHelper.invalidate('cartUuid')

      this.active = false
      this.softLoggedIn = false
      this.profile = _emptyProfile()
      setNewRelicUserId(null)
    },
    async login({ email, password }) {
      const { ApiController, MessageHelper, CookieHelper } = await _getModules()
      try {
        await ApiController.loginCustomer({ email, password })
        CookieHelper.invalidate(SOFT_LOGIN_COOKIE)
        await this.establishSession()
      } catch (e) {
        const { status, cause } = e

        // Expected user errors (wrong credentials, 2FA required) are shown but
        // not reported to the backend log (VIP-12034)
        if (cause === 1011 || cause === 1012) {
          await MessageHelper.addErrorMessage('login_error_invalid_email')
        } else if (cause === 1014) {
          this.presets = {
            email,
            password,
            totpSetupRequired: e.errorData?.totp_setup_required ?? true
          }
          await MessageHelper.addErrorMessage(
            'two_factor_authentication_required'
          )
        } else if (status === 401 || cause === 1013) {
          await MessageHelper.addErrorMessage('please_check_your_data')
        } else if (_isLoginRateLimited(e)) {
          await _showLoginRateLimited(MessageHelper)
        } else {
          await MessageHelper.addErrorMessage(
            'unknown_error',
            null,
            5000,
            e,
            NR_SOURCE_AUTH_LOGIN_UNKNOWN
          )
        }
        throw e
      }
      _runHook(this.hooks.afterLogin)
    },
    async login2FA({ code }) {
      const { email, password } = this.presets
      const { ApiController, MessageHelper } = await _getModules()
      const MESSAGES = {
        401: 'please_check_your_data',
        500: 'server_error'
      }
      try {
        await ApiController.verifyTwoFactorAuth({ email, password, code })
        await ApiController.loginCustomer({ email, password })
        await this.establishSession()
        _runHook(this.hooks.afterLogin)
      } catch (error) {
        const { status, cause } = error
        if (cause === 1021) {
          await MessageHelper.addErrorMessage('invalid_2fa_code')
          await Promise.reject(new Error('invalid_2fa_code'))
        } else if (_isLoginRateLimited(error)) {
          await _showLoginRateLimited(MessageHelper)
        } else {
          await MessageHelper.addErrorMessage(
            MESSAGES[status] || 'unknown_error',
            null,
            5000,
            error,
            NR_SOURCE_AUTH_2FA_UNKNOWN
          )
        }
      }
    },
    async setupTwoFactor() {
      const { email, password } = this.presets
      const { ApiController } = await _getModules()
      return ApiController.getTwoFactorSetup({ email, password })
    },
    async confirmTwoFactorSetup({ code }) {
      const { email, password } = this.presets
      const { ApiController, MessageHelper } = await _getModules()
      try {
        await ApiController.confirmTwoFactorSetup({ email, password, code })
        await ApiController.loginCustomer({ email, password })
        await this.establishSession()
        _runHook(this.hooks.afterLogin)
      } catch (error) {
        const { cause } = error
        if (cause === 1021) {
          await MessageHelper.addErrorMessage('invalid_2fa_code')
        } else if (cause === 1022) {
          await MessageHelper.addErrorMessage('totp_setup_not_initiated')
        } else if (_isLoginRateLimited(error)) {
          await _showLoginRateLimited(MessageHelper)
        } else {
          await MessageHelper.addErrorMessage(
            'unknown_error',
            null,
            5000,
            error,
            NR_SOURCE_AUTH_CONFIRM_2FA_UNKNOWN
          )
        }
        throw error
      }
    },
    async register({
      email,
      firstName,
      lastName,
      gender,
      title,
      password,
      dob
    }) {
      const { ApiController } = await _getModules()

      await ApiController.registerCustomer({
        email,
        firstName,
        lastName,
        gender,
        title,
        password,
        dob
      })

      await ApiController.loginCustomerWithApiKey()
      await this.establishSession()
    },
    async showLogin({ visible, email, hook, hookParams }) {
      this.presets.email = email ?? null
      this.hooks.afterLogin = () => {
        this.isLoginShowing = false
        if (hook && hookParams) hook(...hookParams)
        if (hook && !hookParams) hook()
      }
      this.isLoginShowing = visible ?? false
    },
    async updateCustomerData(data) {
      const { ApiController } = await _getModules()
      await ApiController.updateCustomerData(data)
      await this.fetchProfile()
    },
    setAdminPermissionError(show) {
      this.adminPermissionError = show
    }
  }
})

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [
      { default: SiteConfig },
      { default: TagManager },
      { default: ApiController },
      { default: CookieHelper },
      { default: MessageHelper }
    ] = await Promise.all([
      import('@/services/SiteConfig'),
      import('@/services/analytics/TagManager'),
      import('@/services/ApiController'),
      import('@/services/helpers/CookieHelper'),
      import('@/services/helpers/MessageHelper')
    ])
    cache = {
      SiteConfig,
      TagManager,
      ApiController,
      CookieHelper,
      MessageHelper
    }
    return cache
  }
})()

const _initTagManager = async (profile) => {
  const { TagManager } = await _getModules()
  void TagManager.initCustomerData(profile)
}

// api-v2 RefreshAction: REFRESH_TOKEN_INVALID, REFRESH_TOKEN_CUSTOMER_MISSING
const DEAD_REFRESH_TOKEN_CODES = new Set([1017, 1018])

const _handleRefreshToken = async ({
  profile: { admin_data: admin, customer_id: id },
  softLoggedIn
}) => {
  // no customer or soft login
  if (softLoggedIn) return
  // admin is logged in as customer
  if (admin?.admin_customer_id && admin.admin_customer_id !== id) return

  const { ApiController, CookieHelper } = await _getModules()
  const lastRefresh = Number.parseInt(CookieHelper.getCookie(LAST_REFRESH) || 0)
  const now = Date.now()

  const difference = now - lastRefresh

  if (difference < ACCESS_TOKEN_EXPIRATION) return

  await ApiController.refreshCustomerToken()
}

const _handleSoftLogin = async () => {
  const { CookieHelper } = await _getModules()

  const { groups: grpEmail } =
    /email=(?<email>[^&]*)/.exec(location.search) ?? {}
  // CRM email links carry the customer's public hash (customer_hashes.public_hash) as `hash`
  // rather than `ph`; the soft_login cookie api-v2 reads still needs it under `ph`.
  const { groups: grpHash } =
    /ph=(?<ph>[^&]*)/.exec(location.search) ??
    /(?:^|[?&])hash=(?<ph>[^&]*)/.exec(location.search) ??
    {}

  if (grpEmail && grpHash) {
    const cookie = JSON.stringify({ ...grpEmail, ...grpHash })
    CookieHelper.setCookie(SOFT_LOGIN_COOKIE, cookie, 1)

    return true
  }
  return !!CookieHelper.getCookie(SOFT_LOGIN_COOKIE).length
}

const _handleError = async (e) => {
  console.error(e)
  await _logoutViaPlatform()
}

const _logoutViaPlatform = async () => {
  const { SiteConfig, CookieHelper } = await _getModules()
  CookieHelper.invalidate('cartUuid')

  await nextTick()
  location.replace(
    `https://${SiteConfig.domain}/logout?redirect_uri=${location.pathname}`
  )
}

const _runHook = (hook) => {
  if (typeof hook !== 'function') {
    return
  }
  hook()
}

const _wait = async () =>
  new Promise((resolve) => {
    setTimeout(resolve, 100)
  })
